import { LinearProgress, TablePagination } from '@mui/material'
import qs from 'qs'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DropAlert } from '../../../../components/drops/DropAlert'
import DropCard from '../../../../components/drops/DropCard'
import {
    handleCreatePool,
    handleSetPoolReady,
} from '../../../../components/drops/DropMiniActions'
import { DropPaper } from '../../../../components/drops/DropPaper'
import { ManageDropRamDialog } from '../../../../components/drops/ManageDropRamDialog'
import { ManagePFPFeeDialog } from '../../../../components/drops/ManagePFPFeeDialog'
import { get, post } from '../../../../components/helpers/Api'
import {
    formatWAX,
    setQueryStringWithoutPageReloadPush,
    sleep,
} from '../../../../components/helpers/FormatLinks'
import Page from '../../../../components/page/Page'
import {
    useAtomicCollection,
    useSharedState,
} from '../../../../components/waxplorer/Waxplorer'
import config from '../../../../config.json'
import { EditorBreadCrumbs } from '../../../../components/editor/EditorBreadCrumbs'
import MainButton from '../../../../components/common/util/input/MainButton'

const DropCreatorOverviewPage = (props) => {
    const [state, dispatch] = useSharedState()

    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null

    let values =
        typeof window !== 'undefined'
            ? qs.parse(
                  window.location.search.substring(
                      1,
                      window.location.search.length,
                  ),
              )
            : null

    const pageOptions = [5, 10, 25, 50, 100]

    const { t } = useTranslation('common')

    const collectionName = props.collectionName
    const collectionData = useAtomicCollection(collectionName)
    const [dropData, setDropData] = useState(props.dropData)

    const [ramManagementDialogOpen, setRamManagementDialogOpen] =
        useState(false)

    const [feeManagementDialogOpen, setFeeManagementDialogOpen] =
        useState(false)
    const [callBackCalled, setCallBackCalled] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [initalLoad, setInitalLoad] = useState(true)
    const [feeBalance, setFeeBalance] = useState(0)
    const [totalDrops, setTotalDrops] = useState(null)

    const [errors, setErrors] = useState(null)

    const [curPage, setCurPage] = useState(
        values && values['page'] ? values['page'] : 0,
    )
    const [rows, setRows] = useState(
        values && pageOptions.includes(values['rows']) ? values['rows'] : 5,
    )

    const handleCallBack = (success, error, transactionId) => {
        success && setCallBackCalled(true)
    }

    const parsePage = (p) => {
        const query = { page: p, rows: rows }
        setQueryStringWithoutPageReloadPush(qs.stringify(query))
        setCurPage(parseInt(p))
    }

    const parseRows = (r) => {
        const query = { page: curPage, rows: r }
        setQueryStringWithoutPageReloadPush(qs.stringify(query))
        setRows(parseInt(r))
    }

    const getDropData = async (shouldSleep, page, rows) => {
        setIsLoading(true)
        if (shouldSleep) {
            // sleep is necessary here, because we have to wait a bit until the db syncs up
            await sleep(5000)
        }
        const res = await get(
            'collection-drops/' +
                collectionName +
                `?offset=${page * rows}&limit=${rows}`,
            'legacy',
        )
        return res
    }

    const getTotalDrops = async () => {
        const res = await get(
            'collection-total-drops/' + collectionName + `?limit=${rows}`,
            'wax-api',
        )
        return res
    }

    const mintOnDemandDropExists = true

    const parseDropData = async (res, append = false) => {
        if (res === undefined) {
            setDropData([])
            setIsLoading(false)
            return
        }
        const data = [...res]
        const existingDropData = dropData ? dropData : []
        let temp
        if (append) {
            temp = [...existingDropData, ...data]
        } else {
            temp = [...data]
        }
        setDropData(temp)
        setIsLoading(false)
    }

    const [ramBalance, setRamBalance] = useState()
    const getRamBalance = async () => {
        const body = {
            code: 'nfthivedrops',
            index_position: 1,
            json: 'true',
            key_type: 'i64',
            limit: 1,
            lower_bound: collectionName,
            upper_bound: collectionName,
            reverse: 'true',
            scope: 'nfthivedrops',
            show_payer: 'false',
            table: 'rambalances',
            table_key: '',
        }

        const url =
            process.env.NEXT_PUBLIC_TESTNET === 'TRUE'
                ? config.testapi + '/v1/chain/get_table_rows'
                : state.api + '/v1/chain/get_table_rows'
        const res = await post(url, body)

        if (
            res &&
            res.status === 200 &&
            res.data &&
            res.data.rows &&
            res.data.rows.length > 0
        ) {
            setRamBalance(res.data.rows[0]['byte_balance'])
        } else {
            setRamBalance(0)
        }
    }

    const handleNameDescriptionUpdate = (
        drop,
        name,
        description,
        previewImg,
    ) => {
        dispatch({
            type: 'SET_CALLBACK',
            payload: handleCallBack,
        })
        dispatch({ type: 'SET_ACTION', payload: 'updateDropNameDescr' })
        dispatch({ type: 'SET_COLLECTION_NAME', payload: collectionName })
        dispatch({ type: 'SET_DROP', payload: drop })
        dispatch({
            type: 'SET_CREATE_DROP_DATA',
            payload: {
                previewImg: previewImg,
                description: description,
                name: name,
            },
        })
    }

    const handlePriceUpdate = (drop, dropPriceCurrencyList) => {
        dispatch({
            type: 'SET_CALLBACK',
            payload: handleCallBack,
        })
        dispatch({ type: 'SET_ACTION', payload: 'updateDropPrice' })
        dispatch({ type: 'SET_COLLECTION_NAME', payload: collectionName })
        dispatch({ type: 'SET_DROP', payload: drop })
        dispatch({
            type: 'SET_CREATE_DROP_DATA',
            payload: {
                priceCurrencyList: dropPriceCurrencyList,
            },
        })
    }

    const handleStartEndTimeUpdate = (drop, dropStartDate, dropEndDate) => {
        dispatch({
            type: 'SET_CALLBACK',
            payload: handleCallBack,
        })
        dispatch({ type: 'SET_ACTION', payload: 'updateDropStartEndDate' })
        dispatch({ type: 'SET_COLLECTION_NAME', payload: collectionName })
        dispatch({ type: 'SET_DROP', payload: drop })
        dispatch({
            type: 'SET_CREATE_DROP_DATA',
            payload: {
                startDate: dropStartDate,
                endDate: dropEndDate,
            },
        })
    }

    const handleMaxClaimableUpdate = (
        drop,
        currentMaxClaimable,
        allowInfiniteClaimable,
    ) => {
        dispatch({
            type: 'SET_CALLBACK',
            payload: handleCallBack,
        })
        dispatch({ type: 'SET_ACTION', payload: 'updateDropMaxClaimable' })
        dispatch({ type: 'SET_COLLECTION_NAME', payload: collectionName })
        dispatch({ type: 'SET_DROP', payload: drop })
        dispatch({
            type: 'SET_CREATE_DROP_DATA',
            payload: {
                maxClaimable: currentMaxClaimable ? currentMaxClaimable : 0,
                allowInfiniteClaimable: allowInfiniteClaimable,
            },
        })
    }

    const handleAccountLimitUpdate = (
        drop,
        currentAccountLimit,
        currentAccountLimitCooldown,
    ) => {
        dispatch({
            type: 'SET_CALLBACK',
            payload: handleCallBack,
        })
        dispatch({ type: 'SET_ACTION', payload: 'updateDropAccountLimit' })
        dispatch({ type: 'SET_COLLECTION_NAME', payload: collectionName })
        dispatch({ type: 'SET_DROP', payload: drop })
        dispatch({
            type: 'SET_CREATE_DROP_DATA',
            payload: {
                accountLimitCooldown: currentAccountLimitCooldown
                    ? currentAccountLimitCooldown
                    : 0,
                accountLimit: currentAccountLimit,
            },
        })
    }

    const handleDropReferralUpdate = (drop) => {
        dispatch({
            type: 'SET_CALLBACK',
            payload: handleCallBack,
        })
        dispatch({ type: 'SET_ACTION', payload: 'updateDropReferral' })
        dispatch({ type: 'SET_COLLECTION_NAME', payload: collectionName })
        dispatch({ type: 'SET_DROP', payload: drop })
    }

    const handleDeleteDrop = (drop) => {
        dispatch({
            type: 'SET_CALLBACK',
            payload: handleCallBack,
        })
        dispatch({ type: 'SET_ACTION', payload: 'deleteDrop' })
        dispatch({ type: 'SET_COLLECTION_NAME', payload: collectionName })
        dispatch({ type: 'SET_DROP', payload: drop })
    }

    const createPool = (dropId) => {
        handleCreatePool(dropId, userName, activeUser, setCallBackCalled)
    }

    const setPoolReady = (dropId) => {
        handleSetPoolReady(
            dropId,
            userName,
            activeUser,
            setCallBackCalled,
            setErrors,
        )
    }

    const handleShowBuyers = (dropId) => {
        dispatch({
            type: 'SET_CALLBACK',
            payload: () => {},
        })
        dispatch({ type: 'SET_ACTION', payload: 'showDropBuyers' })
        dispatch({ type: 'SET_COLLECTION_NAME', payload: collectionName })
        dispatch({ type: 'SET_DROP_ID', payload: dropId })
    }

    useEffect(() => {
        if (typeof window !== 'undefined') {
            if (callBackCalled) {
                getDropData(true, curPage, rows).then((res) =>
                    parseDropData(res),
                )
            }
            setCallBackCalled(false)
        }
    }, [callBackCalled])

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setIsLoading(true)
            getDropData(false, curPage, rows).then(parseDropData)
        }
    }, [curPage, rows])

    useEffect(() => {
        if (typeof window !== 'undefined') {
            if (initalLoad) {
                getRamBalance()
                getTotalDrops().then(setTotalDrops)
                setInitalLoad(false)
            }
        }
    }, [initalLoad])

    return (
        <Page id={'DropCreatorOverviewPage'} banners={false}>
            <div className="my-2"></div>
            <EditorBreadCrumbs
                collectionUniqueName={
                    collectionData?.collection_name
                        ? collectionData.collection_name
                        : collectionName
                }
                collectionDisplayName={
                    collectionData?.name ? collectionData.name : collectionName
                }
                path={'dropcreator'}
                pathDisplayName={t('editor.manage_drops')}
            />
            <ManageDropRamDialog
                open={ramManagementDialogOpen}
                setOpen={setRamManagementDialogOpen}
                title={`Manage your RAM for Mint on Demand Drops on the ${collectionName} collection`}
                collectionName={collectionName}
                ramBalance={ramBalance}
                getRamBalance={getRamBalance}
                ramContract={'nfthivedrops'}
            />
            <ManagePFPFeeDialog
                open={feeManagementDialogOpen}
                setOpen={setFeeManagementDialogOpen}
                collectionName={collectionName}
                feeBalance={feeBalance}
                setFeeBalance={setFeeBalance}
            />
            <div className="text-5xl mx-5 mb-10">
                Drop Creator - {collectionName} Overview
            </div>
            <DropPaper className="mx-2 md:mx-5">
                <div className="flex flex-wrap justify-center">
                    <div className="sm:w-60 sm:mx-1 mb-5 sm:mb-0 mx-2">
                        <MainButton
                            size="small"
                            //as="a"
                            className=""
                            href={`/creator/${collectionName}/dropcreator/new`}
                        >
                            {' '}
                            New Drop
                        </MainButton>
                    </div>
                </div>
                {ramBalance !== null ? (
                    <div className="flex justify-center">
                        <div>
                            <div className="mb-2 text-center">
                                Current Mint-on-demand RAM Balance:{' '}
                                <span className="text-primary font-bold">
                                    {ramBalance} Bytes
                                </span>
                            </div>
                            <DropAlert
                                title="Action Required!"
                                condition={
                                    ramBalance === 0 && mintOnDemandDropExists
                                }
                                severity="error"
                            >
                                You have a mint on demand drop, but you don't
                                have any RAM. Users won't be able to claim a
                                single drop! Buy some RAM to make sure users can
                                claim your drops!
                            </DropAlert>
                            <div className="flex justify-center">
                                <MainButton
                                    size="small"
                                    className="w-full sm:w-60"
                                    fullWidth={true}
                                    onClick={() => {
                                        setRamManagementDialogOpen(true)
                                    }}
                                >
                                    Manage RAM
                                </MainButton>
                            </div>
                        </div>
                    </div>
                ) : null}
                {feeBalance !== null ? (
                    <div className="flex justify-center mt-4">
                        <div>
                            <div className="mb-2 text-center">
                                {t('drops.pfp_fee_balance')}:{' '}
                                <span className="text-primary font-bold">
                                    {formatWAX(feeBalance)}
                                </span>
                                <div className="flex justify-center">
                                    <MainButton
                                        size="small"
                                        className="w-full sm:w-60"
                                        fullWidth={true}
                                        onClick={() => {
                                            setFeeManagementDialogOpen(true)
                                        }}
                                    >
                                        {t('drops.manage_fees')}
                                    </MainButton>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}
            </DropPaper>
            <DropAlert
                severity="error"
                title="An error occured"
                condition={errors}
            >
                {errors?.map((error) => (
                    <div>{error}</div>
                ))}
            </DropAlert>
            {isLoading ? (
                <div className="mx-2 md:mx-5">
                    <LinearProgress />
                </div>
            ) : (
                <>
                    <div className="flex flex-wrap justify-around">
                        {dropData &&
                            dropData.map((drop) => {
                                let name = ''
                                let description = ''

                                try {
                                    name = JSON.parse(drop.displayData).name
                                } catch (e) {
                                    name = ''
                                }

                                try {
                                    description = JSON.parse(
                                        drop.displayData,
                                    ).description
                                } catch (e) {
                                    description = ''
                                }
                                return (
                                    <>
                                        <DropCard
                                            dropId={drop.dropId}
                                            collection={drop.collection}
                                            templates={drop.templatesToMint}
                                            name={name}
                                            isPfp={drop.isPFP}
                                            description={description}
                                            drop={drop}
                                            maxClaimable={
                                                drop.maxClaimable
                                                    ? drop.maxClaimable
                                                    : 0
                                            }
                                            numClaimed={
                                                drop.currentClaimed
                                                    ? drop.currentClaimed
                                                    : 0
                                            }
                                            accountLimit={
                                                drop.accountLimit
                                                    ? drop.accountLimit
                                                    : 0
                                            }
                                            accountLimitCooldown={
                                                drop.accountLimitCooldown
                                                    ? drop.accountLimitCooldown
                                                    : 0
                                            }
                                            price={drop.price}
                                            currency={drop.currency}
                                            startDate={drop.startTime}
                                            endDate={drop.endTime}
                                            usesVerification={drop.authRequired}
                                            isPremint={drop.isPremint}
                                            hasPool={drop.hasPool}
                                            assetsInPool={drop.assetsInPool}
                                            poolReady={drop.poolReady}
                                            handleMaxClaimableUpdate={
                                                handleMaxClaimableUpdate
                                            }
                                            handleShowBuyers={handleShowBuyers}
                                            handleAccountLimitUpdate={
                                                handleAccountLimitUpdate
                                            }
                                            handleDropReferralUpdate={
                                                handleDropReferralUpdate
                                            }
                                            handlePriceUpdate={
                                                handlePriceUpdate
                                            }
                                            handleStartEndTimeUpdate={
                                                handleStartEndTimeUpdate
                                            }
                                            handleNameDescriptionUpdate={
                                                handleNameDescriptionUpdate
                                            }
                                            handleDeleteDrop={() =>
                                                handleDeleteDrop(drop)
                                            }
                                            handleCreatePool={createPool}
                                            handleSetPoolReady={setPoolReady}
                                        />
                                    </>
                                )
                            })}
                    </div>
                    {totalDrops ? (
                        <div className="flex justify-center my-10">
                            <TablePagination
                                count={totalDrops?.total}
                                labelRowsPerPage={'Rows per page:'}
                                onPageChange={(e, page) => {
                                    parsePage(page)
                                }}
                                onRowsPerPageChange={(e) => {
                                    parseRows(e.target.value)
                                }}
                                rowsPerPageOptions={pageOptions}
                                page={curPage}
                                rowsPerPage={rows}
                                showFirstButton
                                showLastButton
                                nextIconButtonProps={{ disabled: isLoading }}
                                sx={{ borderBottom: 0 }}
                            />
                        </div>
                    ) : (
                        ''
                    )}
                </>
            )}
        </Page>
    )
}

DropCreatorOverviewPage.getInitialProps = async (ctx) => {
    const c = ctx.query.collection
    const paths = ctx.asPath.split('/')
    const values = qs.parse(paths[paths.length - 1].replace(c + '?', ''))
    values['collectionName'] = c

    return values
}

export default DropCreatorOverviewPage
